/** MVP kosztorys — wejście z wizarda (deterministyczny generator). */

/** Typ prac (krok 0 kreatora) — filtruje widoczne pakiety w kroku zakresu. */
export type WorkType =
  | "malowanie"
  | "gladzie"
  | "tynki"
  | "elewacja"
  | "wylewki"
  | "plytki"
  | "podlogi"
  | "zabudowy_gk"
  | "bialy_montaz";

export const WORK_TYPE_LABELS: Record<WorkType, string> = {
  malowanie: "Malowanie",
  gladzie: "Gładzie",
  tynki: "Tynki maszynowe",
  elewacja: "Elewacja",
  wylewki: "Wylewki",
  plytki: "Płytki",
  podlogi: "Podłogi",
  zabudowy_gk: "Zabudowy GK",
  bialy_montaz: "Biały montaż",
};

export function parseWorkType(raw: unknown): WorkType {
  if (
    raw === "malowanie" ||
    raw === "gladzie" ||
    raw === "tynki" ||
    raw === "elewacja" ||
    raw === "wylewki" ||
    raw === "plytki" ||
    raw === "podlogi" ||
    raw === "zabudowy_gk" ||
    raw === "bialy_montaz"
  ) {
    return raw;
  }
  return "malowanie";
}

/** Jedna lub wiele wartości z zapisu (pojedynczy WorkType albo tablica). */
export function parseWorkTypes(raw: unknown): WorkType[] {
  if (Array.isArray(raw)) {
    const list = raw.map(parseWorkType);
    const seen = new Set<WorkType>();
    const out: WorkType[] = [];
    for (const w of list) {
      if (!seen.has(w)) {
        seen.add(w);
        out.push(w);
      }
    }
    return out.length > 0 ? out : ["malowanie"];
  }
  return [parseWorkType(raw)];
}

/** Legacy — korekta stawek w generatorze (dom/biuro). Nie ustawiane w UI kreatora od workType. */
export type RenovationType = "mieszkanie" | "dom" | "biuro";

export type FinishStandard = "ekonomiczny" | "standard" | "premium";

export type RegionId = "mazowieckie" | "aglomeracja" | "regionalne" | "inne";

/** Identyfikatory zakresu — decydują o grupach pozycji w kosztorysie. */
export type ScopeId =
  | "sciany"
  | "sciany_tylko"
  | "sufit"
  | "podloga"
  | "plytki_ceramiczne"
  | "hydraulika"
  | "elektryka"
  | "naprawy"
  | "montaze";

/** Krótkie nazwy do bloku „przezroczystości” generatora (język wykonawcy). */
export const SCOPE_GROUP_LABELS: Record<ScopeId, string> = {
  sciany: "Malowanie: ściany i sufit (typowy zakres)",
  sciany_tylko: "Malowanie: tylko ściany (bez sufitu w tym pakiecie)",
  sufit: "Malowanie: tylko sufit",
  podloga: "Podłogi (panele / przygotowanie)",
  plytki_ceramiczne: "Płytki ceramiczne / gres (posadzka)",
  hydraulika: "Hydraulika",
  elektryka: "Elektryka",
  naprawy: "Drobne naprawy i przygotowanie",
  montaze: "Drobne montaże i wykończenia",
};

/** Pomieszczenie w kreatorze MVP — metraż, wysokość i zakres prac w tym pokoju. */
export interface Room {
  /** crypto.randomUUID() */
  id: string;
  /** Wolna nazwa (nie enum). */
  name: string;
  areaM2: number;
  /** Domyślnie 2.6 m. */
  wallHeight: number;
  /** Zaznaczone pakiety (`pkg-*`) dla tego pokoju — źródło zaznaczeń w kroku zakresu. */
  selectedScopeItemIds: string[];
  /**
   * Wyliczone z `selectedScopeItemIds` przez `computeScopeIdsFromSelectedItems` —
   * źródło prawdy dla generatora w kontekście tego pokoju.
   */
  scopeIds: ScopeId[];
}

/** Unia zakresów ze wszystkich pokoi (UI, review, checklisty). */
export function allScopeIdsFromRooms(rooms: Room[]): ScopeId[] {
  return [...new Set(rooms.flatMap((r) => r.scopeIds))];
}

export type WizardInput = {
  /** Aktualny krok UI kreatora (0–2: typ prac, metraż, zakres). */
  step: number;
  /** Wybrane typy prac (multi-select w kroku 0); min. jeden do przejścia dalej. */
  workType: WorkType[];
  /**
   * Opcjonalny krótki opis projektu / stref — tylko kontekst, nie blokuje kreatora.
   */
  projectDescription?: string;
  /** Pomieszczenia z metrażem (źródło prawdy zamiast płaskiego m²). */
  rooms: Room[];
  standard: FinishStandard;
  region: RegionId;
  /**
   * Opcjonalna notatka o projekcie — nie mapuje się na pozycje kosztorysu (tylko kontekst / review).
   */
  projectNotes?: string;
};

/**
 * Jedna linia kosztorysu — stabilne `id`, wartości edytowalne inline.
 * Cena końcowa = (robocizna + materiał) × ilość × (1 + marża%).
 */
/** Pochodzenie pozycji — tylko do oznaczenia w UI (generator vs ręczna). */
export type EstimateLineSource = "generator" | "manual";

/** Skąd wzięła się pozycja — pod warstwę trust / proof / review (MVP: domyślne przy wczytaniu). */
export type EstimateLineSourceType =
  | "user_input"
  | "template"
  | "heuristic"
  | "ai_inference"
  | "manual_edit";

export type EstimateLineConfidence = "high" | "medium" | "low";

export type EstimateLineReviewStatus = "draft" | "needs_review" | "confirmed";

/** Grupa sekcji w podglądzie oferty (ClientOfferView) — ustawiana przy szkicu z checklisty. */
export type ChecklistOfferGroup =
  | "malowanie"
  | "podlogi"
  | "hydraulika"
  | "elektryka"
  | "montaze_naprawy";

export type EstimateLine = {
  id: string;
  name: string;
  quantity: number;
  unit: string;
  /** Stawka robocizny za jednostkę (PLN). */
  laborUnitPrice: number;
  /** Stawka materiału za jednostkę (PLN). */
  materialUnitPrice: number;
  /** Marża w % od sumy netto linii (robocizna + materiał) × ilość. */
  marginPercent: number;
  /** Brak / `manual` = pozycja dodana lub zapis sprzed pola — bez etykiety „z generatora”. */
  source?: EstimateLineSource;
  /**
   * Stawka VAT jako ułamek (0, 0.08, 0.23).
   * W MVP synchronizowana globalnym wyborem „Stawka VAT dla oferty” (bez kolumny w tabeli).
   */
  vatRate: number;

  /** Źródło logiczne linii; przy braku w starym zapisie — mapowanie z `source` w `normalizeLinesVatRates`. */
  sourceType?: EstimateLineSourceType;
  /** Krótki opis założeń (np. do przyszłego proof layer). */
  assumptionText?: string | null;
  confidence?: EstimateLineConfidence;
  reviewStatus?: EstimateLineReviewStatus;
  notes?: string | null;
  /** Opcjonalnie: uzasadnienie ilości (np. „m² ścian z heurystyki”). */
  quantityBasis?: string | null;
  /** Szkic z checklisty — jawna sekcja w ofercie (bez ciężkiej klasyfikacji). */
  checklistOfferGroup?: ChecklistOfferGroup;
};

export type SectionHeader = {
  type: "section";
  id: string;
  title: string;
};

export type OfferRow = EstimateLine | SectionHeader;

export function isSectionHeader(row: OfferRow): row is SectionHeader {
  return (row as SectionHeader).type === "section";
}

export function isEstimateLineRow(row: OfferRow): row is EstimateLine {
  return !isSectionHeader(row);
}

/** Teksty oferty edytowalne w podglądzie (MVP — bez rich text, zapis w szkicu). */
export type ClientOfferTextOverrides = {
  /** Główny tytuł dokumentu (np. zamiast „OFERTA REMONTU”). */
  title?: string;
  /** Opcjonalny wiersz pod tytułem. */
  tagline?: string;
  /** Akapit pod sekcją „Oferta obejmuje” (zamiast domyślnego). */
  includesIntro?: string;
  /** Akapit o czasie realizacji (zamiast domyślnego 2–4 tyg.). */
  timelineNote?: string;
  /** Tekst pod blokiem cen / nad stopką (zamiast domyślnego o stawkach rynkowych). */
  priceDisclaimer?: string;
  /** Pierwszy akapit bloku końcowego (kontakt). */
  closingLead?: string;
  /** Drugi akapit bloku końcowego. */
  closingSecond?: string;
};

export type Step3ExtrasIncludeKey =
  | "terms"
  | "aboutUs"
  | "schedule"
  | "process"
  | "variants"
  | "projectManagement";

export type Step3ExtrasIncludes = Record<Step3ExtrasIncludeKey, boolean>;

/**
 * Dodatkowe, opcjonalne sekcje kroku 3 (MVP).
 * Same treści mogą być zapisane nawet gdy `includes[key] === false` (np. użytkownik wyłączył na czas),
 * a do oferty/PDF będzie mapowane osobno.
 */
export type Step3Extras = {
  includes?: Partial<Step3ExtrasIncludes>;
  terms?: string;
  aboutUs?: string;
  schedule?: string;
  process?: string;
  variants?: string;
  projectManagement?: string;
};

/** Dane klienta do nagłówka oferty (trzymane w szkicu / draft bundle). */
export type ClientOfferClientDetails = {
  /** Imię i nazwisko lub nazwa firmy klienta. */
  name: string;
  /** Adres inwestycji. */
  address: string;
  phone?: string;
  email?: string;
};

/** Kto dostarcza materiały (krok 3 — warunki oferty). */
export type MaterialsOwnership = "client" | "contractor" | "shared" | "split" | "tbd";

export const MATERIALS_OWNERSHIP_LABELS: Record<MaterialsOwnership, string> = {
  client: "Po stronie klienta",
  contractor: "Po stronie wykonawcy",
  shared: "Częściowo po każdej stronie",
  split: "Częściowo po każdej stronie",
  tbd: "Do ustalenia",
};

export type OfferValidityDays = "7" | "14" | "30";

export type Step3ScheduleStage = {
  id: string;
  name: string;
  duration: string;
};

export type Step3CleanupOwner = "contractor" | "client" | "shared";

export type Step3PriceVariant = {
  id: "standard" | "comfort" | "premium";
  name: string;
  differences: string;
  materialStandard: string;
  duration: string;
  pricePln: number | null;
  recommended: boolean;
};

export type Step3ProjectMilestone = {
  id: string;
  name: string;
  dateOrWeek: string;
  payment: string;
};

export type Step3OfferExtras = {
  offerValidityDays?: OfferValidityDays;
  startTerm?: string;
  completionTime?: string;
  warrantyNotes?: string;
  assumptionsText?: string;
  companyDescription?: string;
  companyAdvantages?: string[];
  companyReferences?: string;
  scheduleStages?: Step3ScheduleStage[];
  cleanupOwner?: Step3CleanupOwner;
  afterAcceptance?: string;
  showPriceVariants?: boolean;
  priceVariants?: Step3PriceVariant[];
  projectMilestones?: Step3ProjectMilestone[];
  changeRequestRules?: string;
  communicationStandard?: string;
  partyResponsibilities?: string;
};

export function parseMaterialsOwnership(raw: unknown): MaterialsOwnership | undefined {
  if (
    raw === "client" ||
    raw === "contractor" ||
    raw === "shared" ||
    raw === "split" ||
    raw === "tbd"
  ) {
    return raw;
  }
  return undefined;
}

/** Zasady poprawek / gwarancji w ofercie (krok 3). */
export type WarrantyType = "hidden" | "standard" | "custom";

export const WARRANTY_TYPE_LABELS: Record<WarrantyType, string> = {
  hidden: "Nie pokazuj w ofercie",
  standard: "Standardowo: poprawki wad wykonawczych po odbiorze",
  custom: "Własne warunki",
};

export function parseWarrantyType(raw: unknown): WarrantyType | undefined {
  if (raw === "hidden" || raw === "standard" || raw === "custom") {
    return raw;
  }
  return undefined;
}

/** Uproszczony VAT w podsumowaniu (bez backendu). */
export type VatMode = "none" | "8" | "23";
